import { useEffect, useState } from 'react'
import { Building2, Check, Globe, Swords, Target, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import type { GtmRunStageKey } from '@/features/gtm/domain/gtm-run-stages'
import { GtmSpinner } from '@/features/gtm/components/gtm-spinner'
import {
  useInView,
  usePrefersReducedMotion,
} from '@/features/gtm/components/use-in-view'
import { cn } from '@/shared/lib/utils'

/**
 * The demo's analysis, as the workspace shows it: five steps. Writing the
 * first email is outreach work; the next section shows it.
 */
const STEPS: Array<{
  key: Exclude<GtmRunStageKey, 'emails'>
  icon: LucideIcon
}> = [
  { key: 'research', icon: Globe },
  { key: 'competitors', icon: Swords },
  { key: 'campaigns', icon: Target },
  { key: 'companies', icon: Building2 },
  { key: 'decision_makers', icon: Users },
]

/** How long each step stays active while the section plays the run. */
const STEP_MS = 900

/**
 * What a run does, before the visitor runs one: the five analysis steps,
 * played once in order when the section scrolls into view, the way the
 * demo works through them.
 */
export function GtmResultPreview() {
  const { t } = useTranslation('gtm')
  const { ref, inView } = useInView<HTMLOListElement>()
  const reducedMotion = usePrefersReducedMotion()
  const [finished, setFinished] = useState(0)
  const doneCount = reducedMotion ? STEPS.length : finished

  useEffect(() => {
    if (!inView || reducedMotion || finished >= STEPS.length) return
    const timer = window.setTimeout(
      () => setFinished((count) => count + 1),
      STEP_MS,
    )
    return () => window.clearTimeout(timer)
  }, [inView, reducedMotion, finished])

  return (
    <section className="py-10 sm:py-16">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-display font-medium tracking-[-0.01em] text-foreground">
          {t('previewTitle')}
        </h2>
        <p className="mt-3 text-muted-foreground">{t('previewSubtitle')}</p>
      </div>

      <ol ref={ref} className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {STEPS.map(({ key, icon: Icon }, index) => {
          const done = index < doneCount
          const active = inView && index === doneCount
          return (
            <li
              key={key}
              className={cn(
                'relative flex gap-3 rounded-xl border bg-card/85 p-4 transition-[border-color,opacity,box-shadow] duration-500 motion-reduce:transition-none lg:flex-col',
                done && 'border-primary/40',
                active && 'border-primary ring-1 ring-primary',
                !done && !active && 'border-sidebar-border opacity-60',
              )}
            >
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors duration-500 motion-reduce:transition-none',
                  done || active
                    ? 'bg-accent text-primary'
                    : 'bg-secondary/60 text-foreground',
                )}
              >
                <Icon className="size-4" aria-hidden="true" />
              </span>
              <div className="min-w-0 pr-5 lg:pr-0">
                <div className="text-sm font-medium text-foreground">
                  <span
                    data-markdown="skip"
                    className="mr-2 font-mono text-xs text-muted-foreground"
                  >
                    {index + 1}
                  </span>
                  {t(`stage_${key}`)}
                </div>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  {t(`preview_${key}`)}
                </p>
              </div>
              <span
                aria-hidden="true"
                className="absolute top-4 right-4 text-primary"
              >
                {done ? (
                  <Check className="size-4" />
                ) : active ? (
                  <GtmSpinner className="size-4" />
                ) : null}
              </span>
            </li>
          )
        })}
      </ol>
      <div
        aria-hidden="true"
        className="mt-4 h-1 overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-700 ease-out motion-reduce:transition-none"
          style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
        />
      </div>
    </section>
  )
}
